import React from "react";
import PropTypes from "prop-types";
import { Group, Pagination } from "@mantine/core";

const CONTROL_SIZE = 26;
const DOTS_WIDTH = 12;
const GAP = 4;
const EXTRA_WIDTH = 2;

// Approximate rendered width of a page button: digit widths ("1" is narrow) + padding/border
const pageButtonWidth = (pageNum) => {
	const textWidth = String(pageNum)
		.split("")
		.reduce((sum, digit) => sum + (digit === "1" ? 4.5 : 8.5), 0);
	return Math.max(CONTROL_SIZE, textWidth + 15);
};

// Width of the widest page-number layout for this total: near the end, where the numbers are largest
const getItemsMinWidth = (total, siblings) => {
	const maxItems = siblings * 2 + 5;
	const pages =
		total <= maxItems
			? Array.from({ length: total }, (_, i) => i + 1)
			: [1].concat(
					Array.from(
						{ length: maxItems - 2 },
						(_, i) => total - (maxItems - 3) + i
					)
				);
	const dots = total <= maxItems ? 0 : 1;
	const gaps = (pages.length + dots - 1) * GAP;
	const pagesWidth = pages.reduce((sum, p) => sum + pageButtonWidth(p), 0);
	return Math.ceil(pagesWidth + dots * DOTS_WIDTH + gaps + EXTRA_WIDTH);
};

// 1-based page navigation: prev/next, first/last, siblings around current, ellipses between.
// Page numbers are centered in a min-width area so prev/next stay roughly in place.
const PageNavigator = ({ page, totalPages, onChange, siblings, style }) => {
	const total = Math.max(totalPages || 1, 1);
	return (
		<Pagination.Root
			total={total}
			value={page}
			onChange={(p) => (p !== page ? onChange(p) : null)}
			size="sm"
			siblings={siblings}
			boundaries={1}
			color="lxw-blue"
			classNames={{ dots: "PageNavigatorDots" }}
			style={{ lineHeight: 1, ...style }}
		>
			<Group gap={GAP} wrap="nowrap">
				<Pagination.Previous />
				<Group
					gap={GAP}
					wrap="nowrap"
					justify="center"
					style={{ minWidth: getItemsMinWidth(total, siblings) }}
				>
					<Pagination.Items />
				</Group>
				<Pagination.Next />
			</Group>
		</Pagination.Root>
	);
};

PageNavigator.propTypes = {
	page: PropTypes.number.isRequired,
	totalPages: PropTypes.number,
	onChange: PropTypes.func.isRequired,
	siblings: PropTypes.number,
	style: PropTypes.object,
};

PageNavigator.defaultProps = {
	siblings: 1,
};

export default PageNavigator;
